import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listReports } from "../../../api/reports";
import { formatDateTime } from "../../../utils/formatDate";
import type { ReportListItem, ReportListResponse } from "../../../types/report";
import { PRODUCT_LABELS, PHASE_LABELS } from "../../../types/report";
import { ReportStatusBadge } from "../../../components/StatusBadge";
import { LoadingState, ErrorState, EmptyState, DataTable } from "../../../components/TableStates";
import { useFetchData } from "../../../hooks/useFetchData";
import { DocumentIcon, ViewIcon } from "../../../components/Icons";
import { ActionButton } from "../../../components/ActionButton";

const COLUMNS = ["Product", "Phase", "Case ID", "Test Date", "Status", "Assigned", "Actions"] as const;
const PER_PAGE = 20;

export function ReportsPage() {
  const navigate = useNavigate();
  const [searchCaseId, setSearchCaseId] = useState("");
  const [appliedFilter, setAppliedFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);

  const fetchReports = useCallback(
    () => listReports(appliedFilter, page, PER_PAGE),
    [appliedFilter, page],
  );

  const { data, loading, error } = useFetchData<ReportListResponse>(fetchReports, [appliedFilter, page]);

  const items: ReportListItem[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  function handleSearch() {
    setPage(1);
    setAppliedFilter(searchCaseId.trim() || undefined);
  }

  function handleClear() {
    setSearchCaseId("");
    setPage(1);
    setAppliedFilter(undefined);
  }

  return (
    <div>
      {/* Search */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={searchCaseId}
          onChange={(e) => setSearchCaseId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search by Case ID..."
          className="h-10 px-3 rounded-xl border border-stroke-primary bg-bg-primary text-sm text-text-primary placeholder:text-text-quaternary w-64"
        />
        <button
          onClick={handleSearch}
          className="h-10 px-4 rounded-xl bg-btn-action text-text-btn text-sm font-medium"
        >
          Search
        </button>
        {appliedFilter && (
          <button
            onClick={handleClear}
            className="h-10 px-4 rounded-xl bg-bg-tertiary text-text-secondary text-sm font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {loading && <LoadingState columns={7} />}

      {!loading && error && <ErrorState message={error} />}

      {!loading && !error && items.length === 0 && <EmptyState message="No reports found" />}

      {!loading && !error && items.length > 0 && (
        <>
          <DataTable columns={COLUMNS}>
            {items.map((item) => {
              const isReviewable = item.status === "in_review" || item.status === "assigned" || item.status === "drafted";
              return (
                <tr key={item.report_id} className="border-b border-stroke-primary last:border-b-0">
                  <td className="px-4 py-3 text-sm text-text-primary">
                    {PRODUCT_LABELS[item.product_type] ?? item.product_type}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-primary">
                    {PHASE_LABELS[item.cycle_phase] ?? item.cycle_phase}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-primary">{item.case_id}</td>
                  <td className="px-4 py-3 text-sm text-text-primary">
                    {item.test_date ? formatDateTime(item.test_date) : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ReportStatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-text-primary">
                    {item.assigned_at ? formatDateTime(item.assigned_at) : "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isReviewable && (
                      <ActionButton
                        onClick={() => navigate(`/dashboard/results/${item.report_id}`)}
                        icon={<DocumentIcon size={20} />}
                        label="Review"
                      />
                    )}
                    {item.status === "published" && (
                      <ActionButton
                        onClick={() => navigate(`/dashboard/results/${item.report_id}`)}
                        icon={<ViewIcon />}
                        label="View"
                        variant="secondary"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </DataTable>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-text-tertiary">
                Showing {(page - 1) * PER_PAGE + 1}-{Math.min(page * PER_PAGE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="h-9 px-3 rounded-xl bg-bg-tertiary text-text-primary text-sm font-medium disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-sm text-text-primary px-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-9 px-3 rounded-xl bg-bg-tertiary text-text-primary text-sm font-medium disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
